import type { AddToCollectionFormData } from '@/components/ui'
import { guardCard } from '@/hooks/cards/guardCard'
import type { useCardActions } from '@/hooks/cards/useCardActions'
import { useConfirmableAction } from '@/hooks/ui/useConfirmableAction'
import { useToggleHandler } from '@/hooks/ui/useToggleHandler'
import type { CollectionItemCondition } from '@/types'

type Actions = ReturnType<typeof useCardActions>

/** Collection add / update / remove and hide-toggle handlers for one card. */
export function useCardCollectionHandlers(cardId: number | undefined, actions: Actions) {
  const { userFilters, addToCollection, hideCard, unhideCard } = actions
  const updateCollection = actions.updateCollectionByCard
  const removeFromCollection = actions.removeFromCollectionByCard
  const guard = guardCard(cardId)

  const toggleHideHandler = useToggleHandler<number>(
    (id) => userFilters?.hiddenCards?.includes(id) ?? false,
    (id) => unhideCard.mutate(id),
    (id) => hideCard.mutate(id),
  )

  const handleAddToCollection = guard(
    (id, formData: AddToCollectionFormData, options?: { onSuccess?: () => void; onError?: () => void }) => {
      addToCollection.mutate(
        {
          cardId: id,
          data: {
            quantity: formData.quantity,
            notes: formData.notes || undefined,
            acquiredPrice: formData.acquiredPrice ? parseFloat(formData.acquiredPrice) : undefined,
            ...(formData.condition !== undefined ? { condition: formData.condition } : {}),
          },
        },
        options,
      )
    },
  )

  const confirmRemoveFromCollection = useConfirmableAction<void>({
    action: guard((id) => removeFromCollection.mutate(id)),
    confirmMessage: 'Remove this card from your collection?',
  })

  return {
    onToggleHide: guard((id) => toggleHideHandler(id)),
    isTogglingHide: hideCard.isPending || unhideCard.isPending,
    onAddToCollection: handleAddToCollection,
    // Collection quantity/condition edits intentionally do NOT send
    // expectedUpdatedAt. Optimistic concurrency on a frozen timestamp is
    // incompatible with serialized offline replay: two edits queued offline to
    // the same item both bake the same baseline, so on reconnect the second 409s
    // against the first's own write — a spurious "modified by another session".
    // Dropping the guard makes queued same-item edits last-write-wins, matching
    // the offline intent (the backend skips the staleness check when the param
    // is absent). See audit finding #7.
    onUpdateQuantity: guard((id, quantity: number) => updateCollection.mutate({ cardId: id, data: { quantity } })),
    onUpdateCondition: guard((id, condition: CollectionItemCondition | null) =>
      updateCollection.mutate({ cardId: id, data: { condition } }),
    ),
    onRemoveFromCollection: () => confirmRemoveFromCollection(undefined as void),
    isAddingToCollection: addToCollection.isPending,
    isUpdatingCollection: updateCollection.isPending,
    isRemovingFromCollection: removeFromCollection.isPending,
  }
}
